import { useEffect, useState } from 'react';

/**
 * How long a gap without a loader on screen still counts as the same wait. A route change swaps two
 * loaders in one commit, so the real gap is zero; anything longer starts a new wait.
 */
export const WAIT_CONTINUITY_MS = 150;

/**
 * The wait lives in module state because that is its real scope: how long the user has been waiting
 * belongs to the flow, and the flow unmounts the component halfway through. A timer per mount
 * restarts on the redirect and blinks the spinner off.
 */
let mountedLoaders = 0;
let waitStartedAt: number | null = null;
let idleSince: number | null = null;

/**
 * Whether the wait in progress is still the same one: true while any loader is on screen, and for a
 * short window after the last one left, which is what makes a redirect count as one wait.
 */
const isSameWait = (now: number): boolean => {
  if (waitStartedAt === null) return false;
  if (mountedLoaders > 0) return true;

  return idleSince !== null && now - idleSince <= WAIT_CONTINUITY_MS;
};

/** Clears the shared wait. For tests only: module state survives from one test case to the next. */
export const resetSpinnerWait = () => {
  mountedLoaders = 0;
  waitStartedAt = null;
  idleSince = null;
};

/**
 * Whether the spinner should be shown, for a wait that continues across mounts.
 *
 * The delay applies once per wait, so a loader mounted into a wait that already passed it shows the
 * spinner on its first frame and the whole redirect looks like one continuous spinner.
 */
export const useSpinnerDelay = (delayMs: number): boolean => {
  const [isVisible, setIsVisible] = useState(() => {
    // Read during render, before the previous loader is cleaned up: that is what saves the frame.
    const now = Date.now();

    return isSameWait(now) && now - (waitStartedAt as number) >= delayMs;
  });

  useEffect(() => {
    const now = Date.now();
    if (!isSameWait(now)) waitStartedAt = now;
    mountedLoaders += 1;

    const remaining = delayMs - (now - (waitStartedAt as number));
    if (remaining <= 0) setIsVisible(true);

    const timer = remaining > 0 ? window.setTimeout(() => setIsVisible(true), remaining) : undefined;

    return () => {
      if (timer !== undefined) window.clearTimeout(timer);

      mountedLoaders -= 1;
      if (mountedLoaders === 0) idleSince = Date.now();
    };
  }, [delayMs]);

  return isVisible;
};
